import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { AppleIcon, ChevronDownIcon } from 'lucide-react';
import { appStoreListings } from '../../data/appTour';
type Device = 'ios' | 'android';
interface DeviceInfo {
  isMobile: boolean;
  initial: Device;
}
function detectDevice(): DeviceInfo {
  const ua = navigator.userAgent;
  const isIOS = /iPhone|iPad|iPod/.test(ua) || navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  const isAndroid = /Android/.test(ua);
  return {
    isMobile: isIOS || isAndroid,
    initial: isAndroid ? 'android' : 'ios'
  };
}
const devices: {
  id: Device;
  label: string;
}[] = [{
  id: 'ios',
  label: 'iPhone / iPad'
}, {
  id: 'android',
  label: 'Android'
}];
function GooglePlayMark({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" aria-hidden="true" className={className} fill="currentColor" data-id="element-369" data-mp="1ynbed7tcvirx">
      <path d="M4.2 2.3a1.3 1.3 0 0 0-.5 1.1v17.2c0 .5.2.9.5 1.1l9.6-9.7-9.6-9.7Zm10.7 8.6 2.7-2.7L6.1 1.7c-.4-.2-.8-.3-1.1-.2l9.9 9.4Zm0 2.2L5 22.5c.3.1.7 0 1.1-.2l11.5-6.5-2.7-2.7Zm6.2-2.4-2.4-1.4-3 2.7 3 2.7 2.4-1.4c.9-.5.9-2.1 0-2.6Z" data-id="element-370" data-mp="1toq8osvc46w3" />
    </svg>;
}

/**
 * Device chooser with a store button and a QR code that points directly at the
 * store listing. On phones the direct button leads; the QR code is tucked away
 * for scanning from another device.
 */
export function AppDownloadChooser() {
  const [{
    isMobile,
    initial
  }] = useState(detectDevice);
  const [device, setDevice] = useState<Device>(initial);
  const [showQr, setShowQr] = useState(false);
  const url = appStoreListings[device];
  const deviceName = device === 'ios' ? 'iPhone or iPad' : 'Android phone';
  const storeButton = <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex w-full sm:w-auto items-center justify-center gap-3 rounded-2xl bg-charcoal px-6 py-3.5 text-white transition-colors duration-200 ease-out hover:bg-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600 focus-visible:ring-offset-2 focus-visible:ring-offset-white" data-id="element-371" data-mp="f7kawdwcalzp">
      {device === 'ios' ? <AppleIcon aria-hidden="true" className="w-6 h-6" data-id="element-372" data-mp="3fcbcg13iggz1" /> : <GooglePlayMark className="w-5 h-5" data-id="element-373" data-mp="3tbap0adr15eu" />}
      <span className="text-left leading-tight" data-id="element-374" data-mp="3tc40fonnkj3b">
        <span className="block text-[11px] text-white/70" data-id="element-375" data-mp="3p8o1glo426ng">
          {device === 'ios' ? 'Download on the' : 'Get it on'}
        </span>
        <span className="block text-base font-semibold" data-id="element-376" data-mp="7201fa7x49on">
          {device === 'ios' ? 'App Store' : 'Google Play'}
        </span>
      </span>
    </a>;
  const qr = <figure className="flex flex-col items-center" data-id="element-377" data-mp="gtnn0uhsmwi7">
      <div className="rounded-2xl border border-purple-200 bg-white p-4" data-id="element-378" data-mp="gv73al3g97jz">
        <QRCodeSVG value={url} size={176} level="M" fgColor="#1f1f1f" bgColor="#ffffff" title={`QR code to download Fem Entity for ${deviceName}`} data-id="element-379" data-mp="but4zurnhbz5" />
      </div>
      <figcaption className="mt-3 text-sm text-charcoal/65 text-center" data-id="element-380" data-mp="kt4t9q51nxof">
        Scan with your {deviceName} camera
      </figcaption>
    </figure>;
  return <div className="rounded-3xl border border-purple-200 bg-white px-6 py-8 md:px-9 md:py-9 shadow-[0_18px_50px_-30px_rgba(44,44,44,0.5)]" data-id="element-381" data-mp="19fc81v49h8gj">
      <h3 className="font-serif text-2xl text-charcoal" data-id="element-382" data-mp="zhodb56no0yd">Continue in the Fem Entity app</h3>
      <p className="mt-3 text-base text-charcoal/70 leading-relaxed" data-id="element-383" data-mp="zjj0kq5eg55m">
        Choose your device to download Fem Entity. Then sign in with the account you just created to complete your
        practitioner profile and verification.
      </p>

      <div role="group" aria-label="Choose your device" className="mt-7 grid grid-cols-2 gap-1 rounded-full bg-purple-50 p-1" data-id="element-384" data-mp="bsvft4adxl1n">
        {devices.map(option => {
        const active = option.id === device;
        return <button key={option.id} type="button" onClick={() => {
          setDevice(option.id);
          setShowQr(false);
        }} aria-pressed={active} className={`whitespace-nowrap rounded-full px-4 py-2.5 text-sm font-semibold transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600 ${active ? 'bg-purple-700 text-white' : 'text-charcoal/70 hover:text-purple-800'}`} data-id="element-385" data-mp="2s79ywv5aqzu">
              {option.label}
            </button>;
      })}
      </div>

      {isMobile ? <div className="mt-8" data-id="element-386" data-mp="384ks87tc9443">
          {storeButton}
          <button type="button" onClick={() => setShowQr(v => !v)} aria-expanded={showQr} className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-purple-700 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600" data-id="element-387" data-mp="336jdfz2y3qyf">
            Show QR code for another device
            <ChevronDownIcon aria-hidden="true" className={`w-4 h-4 transition-transform duration-200 ease-out ${showQr ? 'rotate-180' : ''}`} data-id="element-388" data-mp="xecj2c7vzw7d" />
          </button>
          {showQr && <div className="mt-5" data-id="element-389" data-mp="2k86fq33c3jl0">{qr}</div>}
        </div> : <div className="mt-8 flex flex-col items-center gap-6" data-id="element-390" data-mp="2p2ao8qs8zzo7">
          {qr}
          <div className="flex w-full items-center gap-3 text-xs text-charcoal/45" data-id="element-391" data-mp="2t2ukzt8wt19t">
            <span className="h-px flex-1 bg-charcoal/10" data-id="element-392" data-mp="1wcv8xotjvkmh" />
            or open on this device
            <span className="h-px flex-1 bg-charcoal/10" data-id="element-393" data-mp="21awseauw1lyh" />
          </div>
          {storeButton}
        </div>}
    </div>;
}